import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, CircularProgress, IconButton, Typography, Snackbar, Alert } from "@mui/material";
import AgentCards from "./AgentCards";
import { ReactComponent as Star1 } from "../../../assets/images/icons/star1_icon.svg";
import { ReactComponent as Star2 } from "../../../assets/images/icons/star2_icon.svg";
import "../../../styles/pageStyles/chatbot/chatWindow.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import config from "../../../config/Config";
import { useNavigate } from "react-router-dom";
import DuplicateApplicationsModal from "../../common/modals/DuplicateApplicationModals";
import {
  fetchNewSessionId,
  extractDownloadUrl,
  extractFileType,
  mapTurnsToMessages,
  normalizeAgent,
  parseAgentFromStatus,
  formatMessageTimestamp
} from "./chatUtils";

const highlightCalqIds = (plainText, keyPrefix) => {
  const CALQ = /\bCALQ\d+\b/gi;
  const result = [];
  let last = 0;
  let m;
  let idx = 0;
  while ((m = CALQ.exec(plainText)) !== null) {
    if (m.index > last) {
      result.push(
        <span key={`${keyPrefix}-cp${idx}`}>
          {plainText.slice(last, m.index)}
        </span>,
      );
    }
    result.push(
      <strong
        key={`${keyPrefix}-calq${idx}`}
        style={{ fontWeight: 700, color: "#067de2" }}
      >
        {m[0]}
      </strong>,
    );
    last = m.index + m[0].length;
    idx++;
  }
  if (last < plainText.length) {
    result.push(
      <span key={`${keyPrefix}-ctail${idx}`}>{plainText.slice(last)}</span>,
    );
  }
  return result.length
    ? result
    : [<span key={`${keyPrefix}-cplain`}>{plainText}</span>];
};

const renderInlineMarkdown = (text, keyPrefix = "") => {
  const TOKEN =
    /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  const parts = [];
  let last = 0;
  let match;
  let i = 0;

  while ((match = TOKEN.exec(text)) !== null) {
    if (match.index > last) {
      const gap = text.slice(last, match.index);
      parts.push(...highlightCalqIds(gap, `${keyPrefix}-g${i}`));
    }
    const [full, , boldItalic, bold, italic, code, linkLabel, linkHref] = match;
    if (boldItalic) {
      parts.push(
        <strong key={`${keyPrefix}-bi${i}`}>
          <em style={{ color: "#067de2" }}>{boldItalic}</em>
        </strong>,
      );
    } else if (bold) {
      parts.push(
        <strong
          key={`${keyPrefix}-b${i}`}
          style={{ fontWeight: 700, color: "#067de2" }}
        >
          {bold}
        </strong>,
      );
    } else if (italic) {
      parts.push(
        <em key={`${keyPrefix}-i${i}`} style={{ color: "#555" }}>
          {italic}
        </em>,
      );
    } else if (code) {
      parts.push(
        <code
          key={`${keyPrefix}-c${i}`}
          style={{
            fontFamily: "monospace",
            fontSize: "12px",
            backgroundColor: "#f0f0f0",
            color: "#c7254e",
            padding: "1px 4px",
            borderRadius: "3px",
          }}
        >
          {code}
        </code>,
      );
    } else if (linkLabel && linkHref) {
      parts.push(
        <a
          key={`${keyPrefix}-a${i}`}
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#067de2",
            textDecoration: "underline",
            wordBreak: "break-all",
          }}
        >
          {linkLabel}
        </a>,
      );
    }
    last = match.index + full.length;
    i++;
  }
  if (last < text.length) {
    parts.push(...highlightCalqIds(text.slice(last), `${keyPrefix}-tail`));
  }
  return parts.length ? parts : highlightCalqIds(text, `${keyPrefix}-plain`);
};

const FormattedMessage = ({ text }) => {
  if (!text) return null;

  const baseStyle = {
    fontFamily: "proxima-regular, Helvetica, Arial, sans-serif",
    fontSize: "14px",
    color: "#111",
    lineHeight: "21px",
  };

  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i];
    const trimmed = raw.trim();

    // ── H2 heading: ## Foo
    if (trimmed.startsWith("## ")) {
      const headingText = trimmed.slice(3);
      elements.push(
        <Typography
          key={`h2-${i}`}
          component="div"
          sx={{
            ...baseStyle,
            fontSize: "15px",
            fontWeight: 700,
            color: "#067de2",
            mt: elements.length > 0 ? "14px" : 0,
            mb: "4px",
            borderBottom: "1px solid #e0e0e0",
            pb: "2px",
          }}
        >
          {renderInlineMarkdown(headingText, `h2-${i}`)}
        </Typography>,
      );
      i++;
      continue;
    }

    // ── H3 / H4 heading: ### Foo / #### Foo
    if (trimmed.startsWith("###") || trimmed.startsWith("####")) {
      const isH4 = trimmed.startsWith("####");
      const headingText = trimmed.slice(isH4 ? 4 : 3);
      elements.push(
        <Typography
          key={`h3-${i}`}
          component="div"
          sx={{
            ...baseStyle,
            fontSize: "14px",
            fontWeight: 700,
            color: "#444",
            mt: elements.length > 0 ? "10px" : 0,
            mb: "2px",
          }}
        >
          {renderInlineMarkdown(headingText, `h3-${i}`)}
        </Typography>,
      );
      i++;
      continue;
    }

    // ── Bullet: - foo  or  • foo  or  * foo
    if (trimmed.match(/^[-•*]\s+/)) {
      const bulletContent = trimmed.replace(/^[-•*]\s+/, "");
      elements.push(
        <Box
          key={`bullet-${i}`}
          sx={{
            display: "flex",
            gap: "8px",
            mt: "5px",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              ...baseStyle,
              minWidth: "14px",
              flexShrink: 0,
              marginTop: "1px",
            }}
          >
            •
          </span>
          <span style={{ ...baseStyle, flex: 1 }}>
            {renderInlineMarkdown(bulletContent, `bullet-${i}`)}
          </span>
        </Box>,
      );
      i++;
      continue;
    }

    // ── Numbered list: 1. foo
    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      elements.push(
        <Box
          key={`num-${i}`}
          sx={{
            display: "flex",
            gap: "8px",
            mt: "5px",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              ...baseStyle,
              minWidth: "14px",
              flexShrink: 0,
              marginTop: "1px",
            }}
          >
            {numberedMatch[1]}.
          </span>
          <span style={{ ...baseStyle, flex: 1 }}>
            {renderInlineMarkdown(numberedMatch[2], `num-${i}`)}
          </span>
        </Box>,
      );
      i++;
      continue;
    }

    // ── Horizontal rule: --- or ***
    if (trimmed.match(/^[-*]{3,}$/)) {
      elements.push(
        <Box
          key={`hr-${i}`}
          sx={{ borderTop: "1px solid #e0e0e0", my: "8px" }}
        />,
      );
      i++;
      continue;
    }

    // ── Empty line → spacing
    if (!trimmed) {
      if (elements.length > 0) {
        elements.push(<Box key={`sp-${i}`} sx={{ height: "6px" }} />);
      }
      i++;
      continue;
    }

    // ── Regular paragraph line
    elements.push(
      <Typography
        key={`p-${i}`}
        component="div"
        sx={{ ...baseStyle, mt: elements.length > 0 ? "5px" : 0 }}
      >
        {renderInlineMarkdown(trimmed, `p-${i}`)}
      </Typography>,
    );
    i++;
  }

  return <Box>{elements}</Box>;
};

const DuplicateGroupsList = ({ groups, auditId, onViewComparison }) => {
  if (!groups?.length) return null;
  return (
    <Box sx={{ mt: "10px" }}>
      {groups.map((group) => (
        <Box
          key={group.group_id}
          sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", mt: "8px" }}
        >
          <Typography
            component="span"
            sx={{ fontFamily: "proxima-regular, Helvetica, Arial, sans-serif", fontSize: "14px", color: "#111" }}
          >
            {group.owner_name}
            {group.property_address ? `, ${group.property_address}` : ""}
          </Typography>
          <Box
            component="span"
            sx={{ px: "10px", py: "2px", borderRadius: "12px", backgroundColor: "#EDEDED", fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}
          >
            {group.total_in_group} Applications
          </Box>
          <Box
            component="button"
            onClick={() => onViewComparison(group, auditId)}
            sx={{ border: "none", background: "none", p: 0, cursor: "pointer", fontSize: "14px", fontWeight: 600, color: "#067de2", textDecoration: "underline" }}
          >
            View Comparison
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const ChatWindow = ({
  userName = "User",
  applicationId = null,
  activeSessionId = null,
  refreshHistoryList,
  sidebarOpen,
}) => {
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [hasConversation, setHasConversation] = useState(false);
  const [sessionId, setSessionId] = useState(activeSessionId || null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [comparisonGroup, setComparisonGroup] = useState(null);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);

  const scrollRef = useRef(null);
  const streamingMessageIdRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [showScrollBottomBtn, setShowScrollBottomBtn] = useState(false);
  const isStreamingRef = useRef(false);
  const isNewSessionRef = useRef(false);
  const sessionIdRef = useRef(null);
  const justCreatedSessionRef = useRef(false);
  const inputRef = useRef(null);

  const fetchDuplicateApplications = async (
  baseUrl,
  sessionId,
  tradeInIds = [],
) => {
  const tokenStr = localStorage.getItem("access_token");

  const params = new URLSearchParams();
  tradeInIds
    .filter((id) => id !== null && id !== undefined && !Number.isNaN(id))
    .forEach((id) => params.append("trade_in_id", id));

  const response = await fetch(
    `${baseUrl}chat/sessions/${sessionId}/duplicate-applications?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${tokenStr}`,
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    let detail;
    try {
      const errBody = await response.json();
      detail = errBody?.detail;
    } catch (e) {
      console.log(e)
    }
    throw new Error(
      detail || `Failed to fetch duplicate applications: ${response.status}`,
    );
  }

  return response.json();
};

  const getTradeInIds = (group) => {
    const idSource =
      group?.all_application_ids ||
      (group?.applications || []).map((app) => app.application_number) ||
      [];
    return idSource
      .map((rawId) => {
        const parsed = parseInt(String(rawId).replace(/^CALQ/i, ""), 10);
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((id) => id !== null);
  };

  const handleViewComparison = async (group, auditId) => {
    const comparisonGroup = { ...group, auditId };
    setComparisonGroup(comparisonGroup);
    setComparisonModalOpen(true);
    setComparisonError(null);
    setComparisonLoading(true);

    try {
      const currentSessionId = sessionIdRef.current || activeSessionId;
      if (!currentSessionId) {
        throw new Error("No active session to load comparison data for.");
      }

      const tradeInIds = getTradeInIds(group);
      const data = await fetchDuplicateApplications(
        config.calqueAI,
        currentSessionId,
        tradeInIds,
      );

      const duplicateGroups = data?.duplicate_groups || [];
      const hasFlatApplicationData =
        duplicateGroups.length > 0 &&
        !Array.isArray(duplicateGroups[0]?.applications);

      let applications = [];

      if (hasFlatApplicationData) {
        applications = duplicateGroups;
      } else {
        const matchingGroup =
          duplicateGroups.find(
            (duplicateGroup) => duplicateGroup.group_id === group.group_id
          ) || duplicateGroups[0];
        applications = matchingGroup?.applications || [];
      }

      setComparisonGroup({
        ...comparisonGroup,
        applications,
      });
    } catch (error) {
      console.error("Failed to fetch duplicate applications:", error);
      setComparisonError(
        error.message || "Failed to load comparison data. Please try again.",
      );
    } finally {
      setComparisonLoading(false);
    }
  };

  const handleCloseComparisonModal = () => {
    setComparisonModalOpen(false);
    setComparisonGroup(null);
    setComparisonError(null);
  };

  const handleMarkDuplicate = (applicationNumbers, group) => {
    const selectedLastFour = applicationNumbers.map((n) => String(n).slice(-4));

    const allIds = (
      group?.all_application_ids ||
      (group?.applications || []).map((app) => app.application_number) ||
      []
    ).map(String);

    const alreadyDuplicateIds = new Set(
      (group?.applications || [])
        .filter((app) => app.is_duplicate === true)
        .map((app) => String(app.application_number)),
    );

    const markedIds = allIds.filter(
      (id) =>
        selectedLastFour.includes(id.slice(-4)) && !alreadyDuplicateIds.has(id),
    );
    const noActionIds = allIds.filter(
      (id) => !markedIds.includes(id) && !alreadyDuplicateIds.has(id),
    );

    if (!markedIds.length) return;

    if (group?.auditId == null) {
      setSnackbarMessage("Audit ID missing.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setComparisonModalOpen(false);

    sendMessage(`Marked as duplicate : ${markedIds.join(", ")}`, {
      cqc_metrics: {
        audit_id: group.auditId,
        group_id: group.group_id,
        marked_as_duplicate_application_ids: markedIds,
        no_action_application_ids: noActionIds,
      },
    });
  };

  const setStreamingState = (val) => {
    isStreamingRef.current = val;
    setIsStreaming(val);
  };

  const setSessionIdSync = (id) => {
    sessionIdRef.current = id;
    setSessionId(id);
  };

  const fetchSession = useCallback(async (sid) => {
    try {
      setIsSessionLoading(true);
      const tokenStr = localStorage.getItem("access_token");
      const response = await fetch(
        `${config.baseUrl}chat/chatbot-messages/${sid}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenStr}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) throw new Error("Session fetch failed");
      const data = await response.json();
      const session = data?.data;
      const mappedMessages = mapTurnsToMessages(session?.turns || []);
      setMessages(mappedMessages);
      setHasConversation(mappedMessages.length > 0);
      setSessionIdSync(session?.session_id || sid);
    } catch (err) {
      console.error("Failed to load session:", err);
    } finally {
      setIsSessionLoading(false);
    }
  }, []);

  useEffect(() => {
    setShowScrollBottomBtn(false);
    isNearBottomRef.current = true;
    if (activeSessionId) {
      if (isStreamingRef.current) return;
      if (
        justCreatedSessionRef.current &&
        sessionIdRef.current === activeSessionId
      ) {
        return;
      }
      justCreatedSessionRef.current = false;
      if (messages.length > 0 && sessionIdRef.current === activeSessionId)
        return;
      setHasConversation(true);
      fetchSession(activeSessionId);
    } else {
      try {
        const saved = sessionStorage.getItem("agentic_chat_state");
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.messages?.length) {
            setMessages(parsed.messages);
            setHasConversation(true);
            setSessionIdSync(parsed.sessionId || null);
            if (parsed.sessionId) {
              navigate(`/calque-ai/${parsed.sessionId}`, { replace: true });
            }
          }
        }
      } catch (e) {}
    }
  }, [activeSessionId, fetchSession, navigate]);

  useEffect(() => {
    if (sessionId && messages.length) {
      sessionStorage.setItem(
        "agentic_chat_state",
        JSON.stringify({ messages, sessionId }),
      );
    }
  }, [messages, sessionId]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 120;
    isNearBottomRef.current = nearBottom;
    setShowScrollBottomBtn(scrollHeight > clientHeight && !nearBottom);
  };

  const scrollToBottomSmooth = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const scrollToBottomInstant = () => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  useEffect(() => {
    if (!scrollRef.current || !isNearBottomRef.current) return;
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (scrollRef.current && isNearBottomRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading && !isStreaming && !statusText && inputRef.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isStreaming, statusText, activeSessionId]);

  const sendMessage = async (text, extraPayload = {}) => {
    if (!text.trim() || isLoading || isStreaming) return;
    const trimmedInput = text.trim();

    let currentSessionId = sessionId;
    if (!currentSessionId) {
      currentSessionId = await fetchNewSessionId(config.calqueAI);
      setSessionIdSync(currentSessionId);
      isNewSessionRef.current = true;
      justCreatedSessionRef.current = true;
    }

    if (!hasConversation) {
      setHasConversation(true);
      if (!activeSessionId) {
        navigate(`/calque-ai/${currentSessionId}`, { replace: true });
      }
    }

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), sender: "user", text: trimmedInput },
    ]);
    setInputValue("");
    setTimeout(() => {
      const textarea = document.querySelector(".chat-input");
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.overflowY = "hidden";
      }
    }, 0);
    setIsLoading(true);
    setStreamingState(true);
    streamingMessageIdRef.current = null;
    setStatusText("Thinking");
    isNearBottomRef.current = true;
    setTimeout(() => scrollToBottomInstant(), 10);

    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await fetch(`${config.calqueAI}chat/query`, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${tokenStr}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: trimmedInput,
          session_id: currentSessionId,
          application_id: applicationId,
          ...extraPayload,
        }),
      });

      if (response.status === 403) {
        localStorage.setItem("access_token", "");
        window.location.href = "/";
        return;
      }
      if (!response.ok) throw new Error("Failed to get response");

      const reader = response.body.getReader();
      let duplicateGroupsData = null;
      let auditIdData = null;
      const decoder = new TextDecoder();
      let accumulatedText = "";
      const lastAgent = [...messages].reverse().find((m) => m.sender === "agent")?.aiAgent || null;
      let currentAgent = lastAgent;
      let serverTimestamp = null;
      const aiMessageId = Date.now() + 1;
      streamingMessageIdRef.current = aiMessageId;
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;
          let event;
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === "metadata") {
            if (event.ai_agent) currentAgent = normalizeAgent(event.ai_agent);
            if (event.timestamp) serverTimestamp = event.timestamp;
          }

          if (event.type === "status") {
            setStatusText(event.text || "Thinking");
            const parsed = parseAgentFromStatus(event.text);
            if (parsed) currentAgent = parsed;
          }

          if (event.type === "token") {
            setStatusText("");
            setIsLoading(false);
            accumulatedText += event.text;
            setMessages((prev) => {
              const exists = prev.find((m) => m.id === aiMessageId);
              const patch = {
                text: accumulatedText,
                aiAgent: currentAgent,
                ...(!exists && { timestamp: serverTimestamp || new Date().toISOString() }),
                ...(duplicateGroupsData && { duplicateGroups: duplicateGroupsData }),
                ...(auditIdData != null && { auditId: auditIdData }),
              };
              if (exists)
                return prev.map((m) => (m.id === aiMessageId ? { ...m, ...patch } : m));
              return [...prev, { id: aiMessageId, sender: "agent", ...patch }];
            });
          }

          if (event.type === "audit_id") {
            auditIdData = event.data?.audit_id ?? null;
            setMessages((prev) => {
              const exists = prev.find((m) => m.id === aiMessageId);
              if (exists)
                return prev.map((m) =>
                  m.id === aiMessageId ? { ...m, auditId: auditIdData } : m,
                );
              return [
                ...prev,
                {
                  id: aiMessageId,
                  sender: "agent",
                  text: accumulatedText,
                  aiAgent: currentAgent,
                  timestamp: serverTimestamp || new Date().toISOString(),
                  auditId: auditIdData,
                },
              ];
            });
          }

          if (event.type === "duplicate_groups") {
            duplicateGroupsData = event.data || [];
            setMessages((prev) => {
              const exists = prev.find((m) => m.id === aiMessageId);
              if (exists)
                return prev.map((m) =>
                  m.id === aiMessageId
                    ? { ...m, duplicateGroups: duplicateGroupsData }
                    : m,
                );
              return [
                ...prev,
                {
                  id: aiMessageId,
                  sender: "agent",
                  text: accumulatedText,
                  aiAgent: currentAgent,
                  timestamp: serverTimestamp || new Date().toISOString(),
                  duplicateGroups: duplicateGroupsData,
                },
              ];
            });
          }

          if (event.type === "done") {
              if (event.ai_agent) {
                currentAgent = normalizeAgent(event.ai_agent);
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === aiMessageId ? { ...m, aiAgent: currentAgent } : m,
                  ),
                );
              }
            setStatusText("");
            setIsLoading(false);
            setStreamingState(false);
            const downloadUrl = extractDownloadUrl(accumulatedText);
            const fileType = downloadUrl
              ? extractFileType(downloadUrl)
              : extractFileType(accumulatedText);
            if (downloadUrl) {
              const cleanText = accumulatedText
                .replace(downloadUrl, "")
                .replace(/\n{2,}/g, "\n")
                .trim();
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === aiMessageId
                    ? {
                        ...m,
                        text: cleanText,
                        downloadUrl,
                        fileType: fileType || "excel",
                      }
                    : m,
                ),
              );
            }
            if (isNewSessionRef.current) {
              if (refreshHistoryList) refreshHistoryList();
              isNewSessionRef.current = false;
            }
            if (event.session_id && event.session_id !== currentSessionId) {
              setSessionIdSync(event.session_id);
            }
          }

          if (event.type === "error") {
            setStatusText("");
            setIsLoading(false);
            setStreamingState(false);
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now() + 2,
                sender: "agent",
                text:
                  event.text ||
                  "Sorry, I encountered an error. Please try again.",
              },
            ]);
          }
        }
      }
      setStatusText("");
      setIsLoading(false);
      setStreamingState(false);
    } catch (error) {
      console.error("ChatWindow send error:", error);
      setStatusText("");
      setIsLoading(false);
      setStreamingState(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          sender: "agent",
          text: "Sorry, I encountered an error. Please try again.",
        },
      ]);
    }
  };

  const handleDownloadFromUrl = async (url, fileType = "excel") => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const acceptHeader =
        fileType === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${tokenStr}`, Accept: acceptHeader },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");
      let filename =
        fileType === "pdf"
          ? `Calque_Report_${new Date().toISOString().slice(0, 10)}.pdf`
          : `Calque_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }
      const mimeType =
        fileType === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const dlUrl = window.URL.createObjectURL(
        new Blob([blob], { type: mimeType }),
      );
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const isInputDisabled =
    isLoading || isStreaming || !!statusText || isSessionLoading;

  if (isSessionLoading) {
    return (
      <Box className="chat-window loading-state">
        <CircularProgress size={28} sx={{ color: "#077EE0" }} />
      </Box>
    );
  }

  return (
    <>
    <Box
      className={`chat-window ${hasConversation ? "in-conversation" : "landing-mode"} ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}
    >
      {!hasConversation ? (
        <Box className="chat-window-content">
          <Box className="sparkle-wrapper">
            <Star1 />
            <Box className="sparkle-small">
              <Star2 />
            </Box>
          </Box>
          <Box className="chat-greeting">
            Hello, <span>{userName}</span>
          </Box>
          <Box className="chat-title">How Can I Assist Today?</Box>
          <Box className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              placeholder="Start typing your request..."
              className="chat-input"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                e.target.style.overflowY =
                  e.target.scrollHeight > 160 ? "auto" : "hidden";
              }}
              onKeyDown={handleKeyDown}
              disabled={isInputDisabled}
              rows={1}
              style={{ overflowY: "hidden" }}
            />
            <button
              className="chat-send-button"
              onClick={() => sendMessage(inputValue)}
              disabled={isInputDisabled || !inputValue.trim()}
            >
              {isLoading || isStreaming || !!statusText ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                "Send"
              )}
            </button>
          </Box>
          <AgentCards onCardClick={(desc) => sendMessage(desc)} />
        </Box>
      ) : (
        <Box className="conversation-layout-canvas">
          <Box
            className="chat-history-scroll-panel"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            <Box className="chat-bubbles-max-width-container">
              {messages.map((msg) => (
                <Box key={msg.id} className={`chat-row-item ${msg.sender}-row`}>
                  {msg.sender === "user" ? (
                    <Box className="bubble user-bubble">
                      <Box component="p" className="user-message-text">
                        {msg.text}
                      </Box>
                    </Box>
                  ) : (
                    <Box className="agent-message-container">
                      <Box className="agent-message-row">
                        <Box className="agent-avatar">
                          <Box className="agent-avatar-icon">
                            <Star1
                              style={{
                                width: "15px",
                                height: "15px",
                                marginLeft: "0.5px",
                              }}
                            />
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <FormattedMessage text={msg.text} />
                            <DuplicateGroupsList
                              groups={msg.duplicateGroups}
                              auditId={msg.auditId}
                              onViewComparison={handleViewComparison}
                            />
                          {msg.downloadUrl && (
                            <Box sx={{ mt: "12px" }}>
                              <button
                                className="chat-download-button"
                                onClick={() =>
                                  handleDownloadFromUrl(
                                    msg.downloadUrl,
                                    msg.fileType,
                                  )
                                }
                              >
                                {msg.fileType === "pdf"
                                  ? "Download PDF"
                                  : "Download Excel"}
                              </button>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      {msg.aiAgent &&
                      !(isStreaming && msg.id === streamingMessageIdRef.current) && (
                        <Typography
                          component="div"
                          sx={{
                            marginTop:"5px",
                            marginLeft:"3px",
                            fontFamily: "proxima-regular, Helvetica, Arial, sans-serif",
                            fontSize: "12px",
                            color: "#8a8a8a",
                          }}
                        >
                          By {`${msg.aiAgent.toUpperCase()} Agent`} | {msg.timestamp && `${formatMessageTimestamp(msg.timestamp)}`}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))}

              {(isLoading || statusText) && (
                <Box className="chat-row-item agent-row">
                  <Box className="loading-message-row">
                    <Box className="agent-avatar">
                      <Box className="agent-avatar-icon">
                        <Star1
                          style={{
                            width: "15px",
                            height: "15px",
                            marginLeft: "0.5px",
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <Box component="span" className="loading-status-text">
                        {statusText || "Thinking"}
                      </Box>
                      <Box className="typing-indicator">
                        {[0, 1, 2].map((i) => (
                          <Box
                            key={i}
                            sx={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: "#077EE0",
                              animation:
                                "bounce 1.4s infinite ease-in-out both",
                              animationDelay: `${[-0.32, -0.16, 0][i]}s`,
                              "@keyframes bounce": {
                                "0%, 80%, 100%": {
                                  transform: "scale(0)",
                                  opacity: 0.5,
                                },
                                "40%": { transform: "scale(1)", opacity: 1 },
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            className="sticky-bottom-input-tray"
            sx={{ position: "relative" }}
          >
            {showScrollBottomBtn && (
              <IconButton
                className="scroll-bottom-button"
                onClick={scrollToBottomSmooth}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: "20px" }} />
              </IconButton>
            )}
            <Box className="chat-input-wrapper">
              <textarea
                ref={inputRef}
                placeholder="Start typing your request..."
                className="chat-input"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                  e.target.style.overflowY =
                    e.target.scrollHeight > 160 ? "auto" : "hidden";
                }}
                onKeyDown={handleKeyDown}
                disabled={isInputDisabled}
                rows={1}
                style={{ overflowY: "hidden" }}
              />
              <button
                className="chat-send-button"
                onClick={() => sendMessage(inputValue)}
                disabled={isInputDisabled || !inputValue.trim()}
              >
                {isLoading || isStreaming || !!statusText ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  "Send"
                )}
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
    <DuplicateApplicationsModal
      open={comparisonModalOpen}
      onClose={handleCloseComparisonModal}
      group={comparisonGroup}
      onMarkDuplicate={handleMarkDuplicate}
      loading={comparisonLoading}
      error={comparisonError}
    />
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
      <Alert 
        onClose={() => setSnackbarOpen(false)} 
        severity={snackbarSeverity}
        sx={{ width: '100%' }}
        variant="filled"
        >
          {snackbarMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ChatWindow;
